import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UserModal from '../../Components/Modals/UserModal';
import Page from '../../Components/Page';
import Button from '../../Components/Utils/Button';
import ReactTable from '../../Components/Utils/ReactTable';
import Tooltip from '../../Components/Utils/Tooltip';
import {useRequestRolesQuery, useRequestUserRolesQuery} from '../../redux/api/roles';
import { useGetUsersQuery } from '../../redux/api/users';
import {  useSelector } from 'react-redux';
import { selectAuthData } from '../../redux/selectors/auth';
import {
  useRequestAllOrganizationsQuery,
  useRequestAllOrganizationsForUserIdQuery,
} from '../../redux/api/organizations';
import { useRequestAllProvidersQuery } from '../../redux/api/providers';
import { useTranslation } from 'react-i18next';
import Icon from '../../Components/Icon/index.jsx';
import { orgUsersFilter, usersFilter } from '../../QueryFilters/queries.js';
import './style.scss';
import { selectUserType } from '../../redux/selectors/userType.js';

function Users() {

  const { user: { id: userId = '' } = {} } = useSelector(selectAuthData);

  const {
    isSuccess: isRolesSuccess,
    isError: isRolesError,
    error: rolesError,
    data: roles
  } = useRequestRolesQuery();

  isRolesError && console.error('There was an error when fetching roles', rolesError);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userData, setUserData] = useState({});
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const userAuthData = useSelector(selectAuthData);
  const userType = useSelector(selectUserType);
  const { data: organizations } = userType?.isSuperAdmin
    ? useRequestAllOrganizationsQuery()
    : useRequestAllOrganizationsForUserIdQuery({
        id: userId,
        filter: JSON.stringify(orgUsersFilter)
    })
  const { data: providers } = useRequestAllProvidersQuery();

  const { isFetching, isError, error, data, refetch: refetchAllUsers } = userType?.isSuperAdmin
    ? useGetUsersQuery({filter: JSON.stringify(usersFilter), mine:false})
    : useGetUsersQuery({filter: JSON.stringify(usersFilter), mine:true})

  const pageCount = data?.pageCount;
  const { t } = useTranslation();
  const getOrgAndRoleNames = (userRole, index = null) => {
    const organizationName = userRole?.organization?.name;
    const roleName = userRole?.role?.name;
    if ((!organizationName && !roleName) || roleName === 'SUPER ADMIN') {
      return null;
    } else return { organizationName, roleName, index };
  };

  const renderRfIdCell = ({ userRfId }) => {
    const rfidTagsCell = [];
    userRfId?.map((item, index) =>
      rfidTagsCell.push(
        <p className='' key={index}>
          <span>{item.value}</span>
        </p>
      )
    );
    return rfidTagsCell;
  };

  const renderOneRole = ({ organizationName, roleName, index }) => (
    <p className='user-roles' key={index + roleName}>
      {organizationName} ( <span>{roleName}</span> )
    </p>
  );
  const renderRoleCell = (userRoles) => {
    const rolesCell = [];
    if (userRoles?.some((role) => role?.role?.name === 'SUPER ADMIN')) {
      rolesCell.push(renderOneRole({ ...userRoles, roleName: 'SUPER ADMIN' }));
    }
    if (userRoles) {
      userRoles?.forEach((userOrgRole, index) => {
        const orgAndRole = getOrgAndRoleNames(userOrgRole, index);
        orgAndRole && rolesCell.push(renderOneRole({ ...orgAndRole, index }));
      });
    } else if (userType.isAdmin) {
      userRoles?.forEach((userRole, index) => {
        for (const userData of userAuthData.providerOrgs) {
          if (
            userRole?.organization?.provider_id &&
            userData.includes(userRole.organization.provider_id)
          ) {
            const orgAndRole = getOrgAndRoleNames(userRole, index);
            rolesCell.push(renderOneRole({ ...orgAndRole, index }));
          }
        }
      });
    } else {
      userRoles?.forEach((userRole, index) => {
        if (userRole.organization && userRole.organization._id === userAuthData.organizationId) {
          const orgAndRoleNames = getOrgAndRoleNames(userRole, index);
          rolesCell.push(renderOneRole(orgAndRoleNames));
        }
      });
    }
    return rolesCell;
  };

  const openEditModal = (modalData = {}) => {
    //Sets modal data to the selected table row
    setUserData(modalData);
    setIsModalOpen(true);
  };

  const toggleAddModal = useCallback(() => {
    setUserData({});
    setIsModalOpen(!isModalOpen);
  }, [isModalOpen]);

  const hiddenColumns = ['id'];

  const columns = useMemo(
    () => [
      {
        Header: t('name'),
        accessor: 'userName',
        // Removed this so the Tooltip is visible
        disableSortBy: true,
        Cell: ({ cell }) => {
          const username = cell.row.values.userName;
          return (
            <Tooltip className='td__username' condition={username?.length > 20}>
              {username}
            </Tooltip>
          );
        }
      },
      {
        Header: t('roles'),
        accessor: 'userOrganizationRoles',
        disableSortBy: true,
        Cell: ({ cell }) => renderRoleCell(cell.row.values.userOrganizationRoles)
      },
      {
        Header: t('email'),
        accessor: 'email',
        className: 'ellipsis'
      },
      {
        Header: 'Rfid',
        className: 'ellipsis',
        accessor: 'rfidTags',
        Cell: ({ cell }) =>
          renderRfIdCell({
            userRfId: cell.row.values.rfidTags
          })
      },

      {
        Header: 'ID',
        accessor: 'id',
        className: 'table-id-cell'
      },
      {
        Header: t('parent_org'),
        accessor: 'parentOrganization',
        className: 'ellipsis',
        Cell: ({ cell }) => {
          return <div>{cell.row.values.parentOrganization?.name}</div>;
        }
      },
      {
        Header: t('actions'),
        accessor: 'actions',
        className: 'table-action-cell',
        Cell: ({ cell }) => (
          <div className={'action_button__container'}>
            <Button
              label={t('history')}
              className='button__charlie'
              onClick={() => {
                navigate('/reports', {
                  state: { userId: cell.row.values.email }
                });
              }}
            />
            <Button
              label={t('edit')}
              className='button__alfa'
              onClick={() => {
                openEditModal(cell.row.values);
              }}></Button>
          </div>
        )
      }
    ],
    [data, t]
  );

  const headerItems = useMemo(
    () => (
      <>
        <div className={'users__search-bar'}>
          <Icon iconName={'search'} iconClass={'users__search_icon'} />
          <input
            className='users__search_input'
            value={searchTerm}
            placeholder={t('search_email_name_ref_id')}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{marginRight: '10px'}}
          />
        </div>
        <Button label={t('add_user')} className='button button__alfa' onClick={toggleAddModal} />
      </>
    ),
    [data, searchTerm]
  );

  const filteredData = useMemo(() => {
    return data?.filter(item => {
      const searchableString = `${item.userName} ${item.email}`.toLowerCase();
      return searchableString.includes(searchTerm.toLowerCase());
    });
  }, [data, searchTerm]);

  const cacheParams = {};
  return (
    <Page title={t('users')} customHeaderItems={headerItems}>
      {isModalOpen && isRolesSuccess && (
        <UserModal
          cacheParams={cacheParams}
          userData={userData}
          refetch={refetchAllUsers}
          roles={roles}
          toggleModal={toggleAddModal}
          userAuthData={userAuthData}
          organizations={organizations}
          providers={providers}
        />
      )}
      <ReactTable
        loading={isFetching}
        hiddenColumns={hiddenColumns}
        data={filteredData ? filteredData : []}
        columns={columns}
        setControlledPageSize={setItemsPerPage}
        setControlledPageIndex={setPage}
        controlledPageCount={pageCount}
      />
    </Page>
  );
}

Users.propTypes = {
  cell: PropTypes.any
};

export default Users;
